import type { Point, Rect } from '../camera';
import type { SceneObject, SpatialIndex } from '../spatial';
import type { Guide } from '../snapping';
import type { Side } from '../connectors';
import type { HandleId } from '../handles';

export type ToolId = 'select' | 'hand' | 'text' | 'sticky' | 'shape' | 'arrow' | 'image' | 'section' | 'frame' | 'connector' | 'table' | 'tasklist' | 'comment';
export type CommandId =
  | 'undo' | 'redo' | 'duplicate' | 'delete' | 'selectAll'
  | 'bringForward' | 'sendBackward'
  | 'zoomFit' | 'zoomSelection' | 'zoomReset' | 'zoomIn' | 'zoomOut'
  | 'renameSection' | 'fitToContent'
  | 'group' | 'ungroup'
  | 'alignLeft' | 'alignHCenter' | 'alignRight' | 'alignTop' | 'alignVCenter' | 'alignBottom'
  | 'distributeH' | 'distributeV' | 'arrange'
  | 'bringToFront' | 'sendToBack' | 'toggleLock'
  | 'copyStyle' | 'pasteStyle'
  | 'palette' // opens the ⌘K command palette (the app's handler)
  | 'nextPage' | 'prevPage' // P14: the board's pages, in order (the app's handler)
  | 'copy' | 'cut' | 'paste'; // clipboard: native events, listed in the help only (never in KEYMAP)
export type Box = { x: number; y: number; w: number; h: number };
/** A connector end: attached to an object at a normalized anchor, or free; `x, y` is its world point either way. */
export type EndInput = { objectId: string | null; anchor: { nx: number; ny: number } | null; x: number; y: number };

/** What the pointer rests on, without a button: an object, one of its handles, anchor dots or plus buttons. */
/** `grab`: a section taken by its contour band, title or grip (P5e): it moves without being selected first. */
export type Hover = { id: string | null; dot: Side | null; handle: HandleId | null; plus: Side | null; grab?: boolean };
export const NO_HOVER: Hover = Object.freeze({ id: null, dot: null, handle: null, plus: null });

/** What a tool asks the application to do. Tools never touch the document: the app applies intents. */
export type Intent =
  | { kind: 'translate'; ids: string[]; dx: number; dy: number; commit: boolean; copy?: boolean } // cumulative since gesture start; copy: Alt-drag duplicates first
  | { kind: 'resize'; id: string; box: Box; commit: boolean }
  | { kind: 'rotate'; ids: string[]; deltaDeg: number; pivot: Point; commit: boolean } // cumulative
  | { kind: 'select'; ids: string[]; mode: 'replace' | 'add' | 'toggle' }
  | { kind: 'marquee'; rect: Rect | null }
  | { kind: 'pan'; dx: number; dy: number }
  | { kind: 'create'; tool: ToolId; at: Point }
  | { kind: 'createBox'; tool: ToolId; box: Box } // world box dragged out with a box tool
  | { kind: 'boxPreview'; tool: ToolId; rect: Rect | null } // the box being dragged out with a box tool (null clears it)
  | { kind: 'connect'; from: EndInput; to: EndInput; commit: boolean }
  | { kind: 'connectOpen'; from: EndInput; at: Point } // released in the void from an object: offer a linked object
  | { kind: 'reconnect'; id: string; which: 'start' | 'end'; to: EndInput; commit: boolean }
  | { kind: 'bend'; id: string; bend: number; commit: boolean } // a connector's middle handle dragged
  | { kind: 'neighbor'; id: string; dir: Side } // a sticky note's plus button clicked
  | { kind: 'notice'; notice: 'locked'; id?: string } // a gesture that cannot do anything: tell the user why (`id`: the locked object)
  | { kind: 'command'; id: CommandId }
  | { kind: 'guides'; guides: Guide[] } // snapping hints for the overlay ([] clears them)
  | { kind: 'hover'; hover: Hover } // the app ignores it when nothing changed
  | { kind: 'cancel' }; // revert the transient state of the current gesture

export type PointerType = 'mouse' | 'pen' | 'touch';
/** Screen pixels relative to the viewport. */
/** `pick`: the section whose title or grip the DOM found under the pointer (outside its box, so no hit test sees it). */
export type PointerInput = { x: number; y: number; pointerType: PointerType; shift: boolean; alt: boolean; meta: boolean; buttons: number; pick?: string };
export type KeyInput = { key: string; code?: string; shift: boolean; alt: boolean; meta: boolean; ctrl?: boolean };

export interface Tool {
  onPointerDown(e: PointerInput): Intent[];
  onPointerMove(e: PointerInput): Intent[];
  onPointerUp(e: PointerInput): Intent[];
  onKey(e: KeyInput): Intent[];
  onCancel(): Intent[];
  /** The pointer moved without a button (at most once per frame); never during a gesture. */
  onHover?(e: PointerInput): Intent[];
}

/** Read-only view of the scene used by tools (the document layer provides it). */
export interface SceneReader {
  objects(): ReadonlyMap<string, SceneObject>;
  index(): SpatialIndex;
  selection(): readonly string[];
  /** The group the user entered by double-clicking (clicks then select inside it); none by default. */
  entered?(): string | null;
  /** World points of a connector's two ends (null for other objects). */
  endpoints?(id: string): { start: Point; end: Point } | null;
  /** World point halfway along a connector's drawing (its bend handle); null for other objects. */
  midpoint?(id: string): Point | null;
  /** The width/height ratio an object must keep when resized (images); null when it is free. */
  aspect?(id: string): number | null;
}
